// mindmetal-app/web/src/components/mindmetal/PersonalityRadar.tsx
import { motion } from 'framer-motion'
import { cn } from '@personic/shared'
import type { BigFivePersonality } from '@personic/shared'

interface PersonalityRadarProps {
  data: BigFivePersonality
  size?: 'sm' | 'md' | 'lg'
  showLabels?: boolean
  showConfidence?: boolean
  className?: string
}

const TRAITS = [
  { key: 'openness', label: 'Openness', short: 'O' },
  { key: 'conscientiousness', label: 'Conscientiousness', short: 'C' },
  { key: 'extraversion', label: 'Extraversion', short: 'E' },
  { key: 'agreeableness', label: 'Agreeableness', short: 'A' },
  { key: 'neuroticism', label: 'Neuroticism', short: 'N' },
] as const

const SIZES = {
  sm: { width: 200, radius: 70, fontSize: 10 },
  md: { width: 300, radius: 110, fontSize: 12 },
  lg: { width: 400, radius: 150, fontSize: 14 },
}

function polarToCartesian(
  centerX: number,
  centerY: number,
  radius: number,
  angleInDegrees: number
): { x: number; y: number } {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  }
}

export function PersonalityRadar({
  data,
  size = 'md',
  showLabels = true,
  showConfidence = true,
  className,
}: PersonalityRadarProps) {
  const { width, radius, fontSize } = SIZES[size]
  const center = width / 2
  const angleStep = 360 / TRAITS.length

  // Generate pentagon background rings
  const rings = [0.2, 0.4, 0.6, 0.8, 1.0]

  // Calculate data points
  const dataPoints = TRAITS.map((trait, i) => {
    const value = data[trait.key]?.value ?? 0
    const confidence = data[trait.key]?.confidence ?? 0
    const angle = i * angleStep
    const point = polarToCartesian(center, center, radius * value, angle)
    const labelPoint = polarToCartesian(center, center, radius + 20, angle)
    return { ...trait, value, confidence, point, labelPoint, angle }
  })

  // Create path for data polygon
  const dataPath =
    dataPoints
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.point.x} ${p.point.y}`)
      .join(' ') + ' Z'

  return (
    <div className={cn('relative', className)}>
      <svg width={width} height={width} viewBox={`0 0 ${width} ${width}`}>
        {/* Background rings */}
        {rings.map((ring) => (
          <polygon
            key={ring}
            points={TRAITS.map((_, i) => {
              const p = polarToCartesian(center, center, radius * ring, i * angleStep)
              return `${p.x},${p.y}`
            }).join(' ')}
            fill="none"
            stroke="currentColor"
            strokeWidth={1}
            className="text-warm-200"
          />
        ))}

        {/* Axis lines */}
        {TRAITS.map((_, i) => {
          const end = polarToCartesian(center, center, radius, i * angleStep)
          return (
            <line
              key={i}
              x1={center}
              y1={center}
              x2={end.x}
              y2={end.y}
              stroke="currentColor"
              strokeWidth={1}
              className="text-warm-200"
            />
          )
        })}

        {/* Data polygon */}
        <motion.path
          d={dataPath}
          fill="currentColor"
          fillOpacity={0.3}
          stroke="currentColor"
          strokeWidth={2}
          className="text-copper-500"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        />

        {/* Data points */}
        {dataPoints.map((point, i) => (
          <motion.circle
            key={point.key}
            cx={point.point.x}
            cy={point.point.y}
            r={4}
            fill="currentColor"
            className="text-copper-600"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: i * 0.1 }}
          />
        ))}

        {/* Labels */}
        {showLabels &&
          dataPoints.map((point) => (
            <text
              key={point.key}
              x={point.labelPoint.x}
              y={point.labelPoint.y}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={fontSize}
              className="fill-midnight-700 font-medium"
            >
              {point.short}
            </text>
          ))}
      </svg>

      {/* Legend with values */}
      {showConfidence && (
        <div className="mt-4 grid grid-cols-5 gap-2 text-xs">
          {dataPoints.map((point) => (
            <div key={point.key} className="text-center">
              <div className="font-medium text-midnight-800">
                {(point.value * 100).toFixed(0)}%
              </div>
              <div className="text-warm-500">
                {point.confidence > 0 ? `±${((1 - point.confidence) * 100).toFixed(0)}%` : '—'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
